import { graphql, useLazyLoadQuery } from 'react-relay';
import { useEffect } from 'react';

import {
  MeaningfulPaintIntervalType,
  PageVersionForLogging,
} from '@watershed/constants/monitoring';
import { Teams } from '@watershed/constants/teams';
import { useOwner } from '@watershed/shared-frontend/hooks/useOwner';
import {
  MEANINGFUL_PAINT_PAGE_NAME,
  useStopMeaningfulPaintTimerAndReportToHoneycomb,
} from '@watershed/shared-frontend/utils/frontendPerformanceMonitoring/measureMeaningfulPaint';
import { CustomSpanAttributes } from '@watershed/shared-universal/constants/otelCustomAttributes';

import { type FlexibleProjectsContentQuery } from './__generated__/FlexibleProjectsContentQuery.graphql';
import { SUPPORTED_ACTION_TYPES_FOR_ROW_ACTIONS } from './FlexibleProjectRowActions';
import FlexibleProjectsList from './FlexibleProjectsList';

// Prepending/appending to lists requires enabling a field as a connection
// which is built on pagination (even though we didn't paginate projects)
// so we need to pass it a first or last argument (hardcoding to upper bounds)
const MAX_NUMBER_OF_PROJECTS = 1000;

// TODO: projects(first: $first) @connection(key: "FlexibleProjectsContent_projects") {
const FlexibleProjectsContentQueryGql = graphql`
  query FlexibleProjectsContentQuery(
    $actionTypes: [ProjectActionType!]
    $first: Int!
  ) @withOwner(owner: DataIngestion) {
    projects(first: $first) {
      __id
      edges {
        node {
          ...FlexibleProjectsList_projects
        }
      }
    }
  }
`;

/**
 * Stops FML (meaningful interval) timer when the basics of the table shows.
 * Starts the last interval timer.
 *
 * We stop timer immediately after table rendered because this table only
 * shows up when basic projects have been loaded.
 */
function useObservabilityLogging(projectsCount: number) {
  useStopMeaningfulPaintTimerAndReportToHoneycomb({
    pageName: MEANINGFUL_PAINT_PAGE_NAME.DI_MEASUREMENT_PROJECTS_PAGE,
    intervalType: MeaningfulPaintIntervalType.FirstMeaningfulInterval,
    // Log that this is the new page so we can compare data
    additionalProperties: {
      [CustomSpanAttributes.INGESTION_PAGE_VERSION]:
        PageVersionForLogging.VersionB,
      [CustomSpanAttributes.WEB_VITALS_ADDITIONAL_PROPERTIES_FETCHED_ROW_COUNT]:
        projectsCount,
    },
  });
}

/**
 * If not loaded done loading, it shows a skeleton in its place with Suspense.
 */
export default function FlexibleProjectsContent({
  setConnectionId,
}: {
  setConnectionId: (connectionId: string | null) => void;
}) {
  useOwner(Teams.DataIngestion);

  const queryData = useLazyLoadQuery<FlexibleProjectsContentQuery>(
    FlexibleProjectsContentQueryGql,
    {
      actionTypes: SUPPORTED_ACTION_TYPES_FOR_ROW_ACTIONS,
      first: MAX_NUMBER_OF_PROJECTS,
    }
  );
  useObservabilityLogging(queryData.projects.edges.length);

  // TODO: get connection id and pass it a different way
  const connectionId = queryData.projects.__id ?? null;
  useEffect(() => {
    setConnectionId(connectionId);
  }, [connectionId, setConnectionId]);

  // TODO: any edges that are null had an error, so we should show those
  // errors to the user in a usable way without breaking the whole page
  const projectsRef = queryData.projects.edges
    .map((edge) => edge?.node || null)
    .filter((node): node is NonNullable<typeof node> => node !== null);

  return <FlexibleProjectsList projectsRef={projectsRef} />;
}
